import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import CartFloatingButton from "../components/CartFloatingButton";
import "./BookingPage.css";

/* ─────────────────────────────────────────────
   Helper: find highest active override price per day for a specific bike
───────────────────────────────────────────── */
function getOverridePricePerDay(bikeId, overrides, startDate, endDate) {
  if (!overrides || overrides.length === 0 || !bikeId || !startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  let bestOverride = null;

  for (const o of overrides) {
    if (!o.isActive) continue;
    const oStart = new Date(o.startDate);
    const oEnd = new Date(o.endDate);
    if (oStart <= end && oEnd >= start) {
      const targetBikeId =
        o.bikeId?._id ||
        o.bikeId ||
        (o.bikeIds?.[0]?._id ? o.bikeIds[0]._id : o.bikeIds?.[0]);

      const isSpecificToThisBike = targetBikeId && String(targetBikeId) === String(bikeId);
      const isGlobal = !targetBikeId && (!o.bikeIds || o.bikeIds.length === 0);

      if (isSpecificToThisBike) {
        if (bestOverride === null || !bestOverride.isSpecific || o.pricePerDay > bestOverride.price) {
          bestOverride = { price: o.pricePerDay, isSpecific: true };
        }
      } else if (isGlobal) {
        if (bestOverride === null || (!bestOverride.isSpecific && o.pricePerDay > bestOverride.price)) {
          bestOverride = { price: o.pricePerDay, isSpecific: false };
        }
      }
    }
  }

  return bestOverride ? bestOverride.price : null;
}

/* ─────────────────────────────────────────────
   Helper: calculate total given pricePerDay & hours
───────────────────────────────────────────── */
function calculateTotal(pricePerDay, diffHours) {
  if (diffHours <= 26) return pricePerDay;
  const fullDays = Math.floor(diffHours / 24);
  const extraHours = diffHours % 24;
  let extraCharge = 0;
  if (extraHours <= 2) extraCharge = 0;
  else if (extraHours <= 4) extraCharge = 0.25 * pricePerDay;
  else if (extraHours <= 6) extraCharge = 0.50 * pricePerDay;
  else extraCharge = pricePerDay;
  return (fullDays * pricePerDay) + extraCharge;
}

function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { cartItems, addToCart, setCartOpen } = useCart();

  const bikeId   = searchParams.get("bike");
  const startDate = searchParams.get("start");
  const endDate   = searchParams.get("end");

  const [bike, setBike] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [effectivePricePerDay, setEffectivePricePerDay] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  // Derive addedToCart from live DB-backed cartItems
  const addedToCart = cartItems.some(
    (i) =>
      (i.bikeId === bikeId || String(i.bikeId) === bikeId) &&
      new Date(i.startDate).toISOString() === new Date(startDate).toISOString() &&
      new Date(i.endDate).toISOString()   === new Date(endDate).toISOString()
  );

  // Fetch bike details
  useEffect(() => {
    axiosClient
      .get(`/bikes/${bikeId}`)
      .then((res) => setBike(res.data))
      .catch((err) => console.error("Bike fetch error:", err));
  }, [bikeId]);

  // Fetch applicable price overrides
  useEffect(() => {
    if (!startDate || !endDate) return;
    axiosClient
      .get(`/price-overrides/range?startDate=${startDate}&endDate=${endDate}`)
      .then((res) => setOverrides(res.data.overrides || []))
      .catch(() => setOverrides([]));
  }, [startDate, endDate]);

  // Calculate total amount
  useEffect(() => {
    if (bike && startDate && endDate) {
      const diffHours = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60);
      const overridePPD = getOverridePricePerDay(bikeId, overrides, startDate, endDate);
      const usedPPD = overridePPD !== null ? overridePPD : bike.pricePerDay;
      setEffectivePricePerDay(usedPPD);
      setTotalAmount(Math.round(calculateTotal(usedPPD, diffHours)));
    }
  }, [bike, bikeId, overrides, startDate, endDate]);

  // Sync addedToCart with cart state (no-op: derived above)

  /* ── Add to Cart (async — persists to DB) ── */
  const handleAddToCart = async () => {
    if (!token) {
      toast.error("Please log in to add to cart.");
      return navigate("/login");
    }
    if (!bike) return;

    try {
      setAddingToCart(true);
      await addToCart({
        bikeId,
        bikeName:             bike.name,
        bikeImage:            bike.image,
        bikeType:             bike.type,
        startDate,
        endDate,
        pricePerDay:          bike.pricePerDay,
        effectivePricePerDay: effectivePricePerDay ?? bike.pricePerDay,
        totalAmount,
      });
      toast.success("Added to cart! Proceed to payment when ready.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  /* ── Proceed to Payment moved to CartDrawer ── */


  if (!bike) return (
    <div className="booking-page">
      <LoadingSpinner message="Loading bike details" sub="Preparing your booking summary…" />
    </div>
  );

  const bikeUnavailable = !bike.isAvailable || bike.isBlocked;
  const isSurgeActive   = effectivePricePerDay !== null && effectivePricePerDay !== bike.pricePerDay;

  // Derived values
  const diffHours = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60);
  const diffDays  = Math.max(1, Math.floor(diffHours / 24));

  // Spec rows (only render if value exists)
  const specs = [
    bike.engineCapacity   && { label: "Engine Capacity",    value: bike.engineCapacity },
    bike.weight           && { label: "Weight",             value: bike.weight },
    bike.mileage          && { label: "Mileage",            value: bike.mileage },
    bike.fuelTankCapacity && { label: "Fuel Tank Capacity", value: bike.fuelTankCapacity },
  ].filter(Boolean);

  return (
    <div className="booking-page">
      {addingToCart && (
        <LoadingSpinner overlay message="Adding to Cart" sub="Saving bike to your cart…" />
      )}

      <CartFloatingButton />

      <div className="booking-header">
        <button className="page-back-btn" onClick={() => navigate(-1)} title="Go back">
          <span className="back-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </span>
          Back
        </button>
        <h1>Booking Summary</h1>
      </div>

      <div className="checkout-container">
        {/* ── Image ── */}
        <div className="checkout-image-section">
          <img
            src={
              bike.image?.startsWith("http")
                ? bike.image
                : `https://bros-bike-rental-api.onrender.com/${bike.image}`
            }
            alt={bike.name}
          />

          {/* Specs overlay */}
          {specs.length > 0 && (
            <div className="specs-strip">
              {specs.map((s) => (
                <div key={s.label} className="spec-item">
                  <span className="spec-label">{s.label}</span>
                  <span className="spec-value">{s.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Content ── */}
        <div className="checkout-content">
          {/* Title */}
          <div className="bike-title-row">
            <div>
              <h2>{bike.name}</h2>
              {bike.totalUnits > 0 && (
                <span className="available-count">
                  <span className="avail-dot" />
                  {bike.totalUnits} Available
                </span>
              )}
            </div>
            <span className="bike-type-badge">{bike.type}</span>
          </div>

          {/* Color swatches */}
          {bike.colors?.length > 0 && (
            <div className="color-row">
              <span className="color-row-label">Available Colours</span>
              <div className="color-swatches">
                {bike.colors.map((c, i) => (
                  <span
                    key={i}
                    className="color-swatch"
                    style={{ background: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="booking-dates">
            <div className="date-box">
              <span className="date-label">Pickup</span>
              <span className="date-value">
                {new Date(startDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
                <br />
                {new Date(startDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <div className="dates-arrow">→</div>
            <div className="date-box">
              <span className="date-label">Drop-off</span>
              <span className="date-value">
                {new Date(endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
                <br />
                {new Date(endDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>

          {/* Price section */}
          <div className="price-section">
            <p className="price-section-heading">Price for selected dates</p>

            {/* Regular price with +40% strike */}
            {!isSurgeActive ? (
              <div className="price-line">
                <span className="price-line-label">Price</span>
                <span className="price-line-value">
                  ₹{bike.pricePerDay}/day
                  <span
                    style={{
                      marginLeft: "8px",
                      textDecoration: "line-through",
                      color: "var(--bp-muted)",
                      fontSize: "0.88rem",
                      fontWeight: 500,
                    }}
                  >
                    ₹{Math.round(bike.pricePerDay * 1.4)}/day
                  </span>
                </span>
              </div>
            ) : (
              <div className="price-line surge-line">
                <span className="price-line-label">Special Price</span>
                <span className="price-line-value surge-value">
                  ₹{effectivePricePerDay}/day
                  <span
                    style={{
                      marginLeft: "8px",
                      textDecoration: "line-through",
                      color: "var(--bp-muted)",
                      fontSize: "0.88rem",
                      fontWeight: 500,
                    }}
                  >
                    ₹{Math.round(effectivePricePerDay * 1.4)}/day
                  </span>
                </span>
              </div>
            )}


            {/* Surge notice */}
            {isSurgeActive && (
              <div className="surge-notice">
                🔥 Special pricing is applied for the selected dates.
              </div>
            )}

            {/* Total */}
            <div className="total-row">
              <span className="total-label">Total</span>
              <span className="total-value">₹{totalAmount}</span>
            </div>

            {/* Unlimited kms tag */}
            <div className="unlimited-tag">✓ Unlimited Kms</div>
          </div>

          {/* Pricing tiers (hourly / daily / weekend) */}
          {(bike.hourlyPrice > 0 || bike.pricePerDay > 0 || bike.weekendPrice > 0) && (
            <div className="pricing-tiers">
              {bike.hourlyPrice > 0 && (
                <div className="tier">
                  <span className="tier-label">HOURLY</span>
                  <span className="tier-value">₹ {bike.hourlyPrice}</span>
                </div>
              )}
              <div className="tier">
                <span className="tier-label">DAILY</span>
                <span className="tier-value">₹ {bike.pricePerDay}</span>
              </div>
              {bike.weekendPrice > 0 && (
                <div className="tier">
                  <span className="tier-label">WEEKEND</span>
                  <span className="tier-value">₹ {bike.weekendPrice}</span>
                </div>
              )}
            </div>
          )}

          {/* Unavailable notice */}
          {bikeUnavailable && (
            <div className="unavailable-notice">
              🔒 This bike is currently unavailable for online booking.
            </div>
          )}

          {/* ── CTA Buttons ── */}
          {!bikeUnavailable && (
            <div className="cta-buttons">
              {!addedToCart ? (
                <button
                  className="add-to-cart-btn"
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                >
                  {addingToCart ? "Adding…" : "🛒 Add to Cart"}
                </button>
              ) : (
                <button
                  className="in-cart-btn"
                  onClick={() => setCartOpen(true)}
                >
                  ✓ Added to Cart — Open Cart to Pay
                </button>
              )}
            </div>
          )}

          {bikeUnavailable && (
            <button
              className="payment-btn"
              disabled
              style={{ opacity: 0.5, cursor: "not-allowed", marginTop: 16 }}
            >
              Bike Unavailable
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingPage;
